import { Pic } from "./Pic";

import { motion } from "framer-motion";
import { Factory, Wind, Volume2, FlaskConical, Leaf, ShieldCheck, HeartPulse, Sparkles } from "lucide-react";
import { MaskLines, Reveal, SectionLabel } from "./Motion";
import { useT } from "@/i18n";

const OUTSIDE = [[Factory, "Smog"], [Wind, "Polveri sottili"], [Volume2, "Rumori"], [FlaskConical, "Agenti chimici"]];

const PILLARS = [
  [Leaf, "Materiali naturali e green", "Il legno è il protagonista: un materiale naturale e rinnovabile, che aiuta a regolare l'umidità e rende l'aria di casa più piacevole."],
  [ShieldCheck, "Nessuna sostanza nociva", "Scegliamo con attenzione ogni colla, vernice, finitura e trattamento: nella tua casa non entra nessun prodotto nocivo per le persone."],
  [HeartPulse, "Le regole sanitarie più severe", "Materiali e lavorazioni seguono le più stringenti regole sanitarie, per un'aria interna pulita in ogni stanza."],
  [Sparkles, "Un ambiente armonico", "Legno, luce naturale e silenzio: uno spazio che aiuta corpo e mente a rigenerarsi ogni giorno."],
];

const Pillar = ({ p, i, t }) => {
  const [Icon, title, text] = p;
  return (
    <motion.li
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.8, delay: (i % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
      className="border-t border-[#2F4A1C]/25 pt-6"
      data-testid={`healthy-pillar-${i}`}
    >
      <Icon className="h-6 w-6 text-[#4E7A1E]" />
      <h3 className="mt-4 font-display text-2xl font-bold leading-tight tracking-tight">{t(`healthy.p${i}.t`, title)}</h3>
      <p className="mt-3 text-base leading-relaxed text-[#3D4536]">{t(`healthy.p${i}.d`, text)}</p>
    </motion.li>
  );
};

export const HealthyHome = () => {
  const t = useT();
  return (
    <section id="case-salutari" className="relative overflow-hidden bg-[#E9EEE0] px-5 py-28 text-[#121110] sm:px-8 lg:py-40" data-testid="healthy-section">
      <div className="mx-auto grid max-w-[1500px] gap-16 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionLabel index="03">{t("healthy.label", "Case salutari")}</SectionLabel>
          <MaskLines
            className="mt-10 font-display text-4xl font-bold leading-[1.02] tracking-tight sm:text-5xl lg:text-[4.4rem]"
            lines={[t("healthy.h1", "Fuori, smog e rumore."), <span key="b">{t("healthy.h2a", "A casa,")} <em className="font-serif font-normal text-[#4E7A1E]">{t("healthy.h2em", "rigenerazione.")}</em></span>]}
          />
          <Reveal delay={0.2} className="mt-8 max-w-2xl space-y-4 text-base leading-relaxed text-[#3D4536] sm:text-lg">
            <p>{t("healthy.intro1", "Ogni giorno, nelle ore che passiamo fuori casa, siamo esposti ad agenti inquinanti di ogni tipo. Non possiamo cambiare il mondo là fuori, ma possiamo cambiare il luogo in cui torniamo.")}</p>
            <p className="font-semibold text-[#121110]">{t("healthy.intro2", "Per questo tutte le nostre case sono green: un ambiente salubre e armonico, a contatto con prodotti naturali, dove l'essere umano può davvero rigenerarsi.")}</p>
          </Reveal>
          <Reveal delay={0.25} className="mt-12 grid gap-6 sm:grid-cols-2" data-testid="healthy-compare">
            <div className="border border-[#121110]/15 bg-[#F7F5F0]/60 p-6">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#6E685E]">{t("healthy.out", "Fuori casa")}</p>
              <ul className="mt-4 grid grid-cols-2 gap-3">
                {OUTSIDE.map(([Icon, label], i) => (
                  <li key={label} className="flex items-center gap-2 text-sm text-[#6E685E] line-through decoration-[#B3412E]/60" data-testid={`healthy-outside-${i}`}>
                    <Icon className="h-4 w-4 shrink-0" /> {t(`healthy.o${i}`, label)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="keep-dark bg-[#2F4A1C] p-6 text-[#F1F5EA]">
              <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#B9D58F]">{t("healthy.in", "In una casa Emma")}</p>
              <p className="mt-4 text-base leading-relaxed">{t("healthy.inText", "Legno naturale, aria pulita, silenzio. Nessuna colla, vernice o sostanza nociva per la salute.")}</p>
            </div>
          </Reveal>
        </div>
        <Reveal className="lg:col-span-5" delay={0.1}>
          <div className="relative overflow-hidden">
            <Pic src="/img/emma/casa-salutare.jpg" alt={t("healthy.imgAlt", "Persona che si rilassa con una tazza sulle scale di una casa in legno luminosa")} loading="lazy" className="aspect-[4/5] w-full object-cover" />
            <p className="keep-dark absolute bottom-0 left-0 right-0 bg-gradient-to-t from-[#121110]/85 to-transparent p-6 pt-16 font-serif text-2xl italic text-[#F7F5F0] sm:text-3xl" data-testid="healthy-quote">
              {t("healthy.quote", "Almeno a casa, respira.")}
            </p>
          </div>
        </Reveal>
        <ul className="grid gap-x-12 gap-y-12 sm:grid-cols-2 lg:col-span-12 lg:grid-cols-4">
          {PILLARS.map((p, i) => <Pillar key={p[1]} p={p} i={i} t={t} />)}
        </ul>
      </div>
    </section>
  );
};
